/**
 * SecurityCard on /settings/account (Plan 04 + Plan 50; moved off
 * /profile per PROD-27).
 *
 * Two surfaces, top-down:
 *
 *  1. Email row + a "Change email" dialog (new email + confirm-new-email).
 *     Submit hits POST /api/me/email-change, which emails a verification
 *     link TO THE NEW ADDRESS; the /verify-email-change page consumes the
 *     token. Until the @ordo/schema email-change engine lands (Lane A
 *     integration), the server answers 501 and the dialog surfaces that
 *     message verbatim.
 *
 *  2. Manage sign-in methods — magic-link (always available),
 *     password (set / change via POST /api/me/password), and Google
 *     OAuth (link via GET /api/auth/link-google → Multipass link mode).
 *     Statuses come from GET /api/me/sign-in-methods. The link-google
 *     round-trip lands back on /settings/account with a `googleLink`
 *     query param that we surface as a toast. Unlinking Google is out
 *     of scope.
 *
 * Per the Plan 04 V1 TOTP deferral, the card does NOT include a
 * Two-Factor Authentication section — not even a "coming soon"
 * placeholder. When Multipass exposes TOTP enrollment, the section
 * lands in a follow-on plan.
 */

import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { FormDialog } from '@ordo/ui/form-dialog'
import { FormField } from '@ordo/ui/form-field'
import { Input } from '@ordo/ui/input'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

import { useRequestEmailChangeMutation, useSignInMethodsQuery } from '@/api/me'
import { isPlausibleEmail } from '@/components/family/helpers'
import { SetPasswordDialog } from '@/components/settings/set-password-dialog'

export interface SecurityCardProps {
  email: string | null
}

export function SecurityCard({ email }: SecurityCardProps) {
  const [changeDialogOpen, setChangeDialogOpen] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [confirmNewEmail, setConfirmNewEmail] = useState('')
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const requestMutation = useRequestEmailChangeMutation()
  const { data: methods, isPending: methodsPending } = useSignInMethodsQuery()
  const [searchParams, setSearchParams] = useSearchParams()

  const trimmedNewEmail = newEmail.trim()
  const emailsMatch = trimmedNewEmail !== '' && trimmedNewEmail === confirmNewEmail.trim()
  const emailLooksValid = isPlausibleEmail(trimmedNewEmail)
  // Inline error once the user has typed something that isn't a valid
  // address — empty stays quiet so the field doesn't nag before entry.
  const newEmailError = trimmedNewEmail !== '' && !emailLooksValid ? 'Enter a valid email address.' : undefined

  // Landing leg of the Google-link round-trip: /api/auth/link-google/callback
  // redirects back here with ?googleLink=linked|error.
  useEffect(() => {
    const outcome = searchParams.get('googleLink')
    if (!outcome) {
      return
    }
    if (outcome === 'linked') {
      toast.success('Google account linked')
    } else {
      toast.error("Couldn't link Google account")
    }
    setSearchParams(
      (params) => {
        params.delete('googleLink')
        return params
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Security &amp; sign-in</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Email</p>
            <p className="truncate text-xs text-muted-foreground">{email ?? 'Not set'}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setChangeDialogOpen(true)}>
            Change email
          </Button>
        </div>

        <Separator />

        <div>
          <h3 className="mb-3 text-sm font-medium">Sign-in methods</h3>
          <div className="grid gap-3">
            <MethodRow
              label="Sign-in link"
              detail={email ? `We'll email a link to ${email}` : "We'll email a link to your address"}
              status="Always available"
            />
            <MethodRow
              label="Password"
              detail="Set a password so you can sign in without waiting for an email link."
              status={methodsPending ? <Skeleton className="h-3 w-14" /> : methods?.hasPassword ? 'Set' : 'Not set'}
              action={
                <Button
                  variant="outline"
                  size="sm"
                  disabled={methodsPending}
                  onClick={() => setPasswordDialogOpen(true)}>
                  {methods?.hasPassword ? 'Change password' : 'Set password'}
                </Button>
              }
            />
            <MethodRow
              label="Google"
              detail="Use your Google account to sign in faster."
              status={
                methodsPending ? <Skeleton className="h-3 w-14" /> : methods?.googleLinked ? 'Linked' : 'Not linked'
              }
              action={
                methods?.googleLinked ? undefined : (
                  <Button asChild variant="outline" size="sm">
                    <a href="/api/auth/link-google">Link Google</a>
                  </Button>
                )
              }
            />
          </div>
        </div>
      </CardContent>
      <FormDialog
        open={changeDialogOpen}
        onOpenChange={setChangeDialogOpen}
        title="Change email"
        description="We'll send a verification link to the new address. Your current email keeps working until you confirm."
        submitLabel="Send verification link"
        submitDisabled={!emailLooksValid || !emailsMatch || requestMutation.isPending}
        onSubmit={async () => {
          await requestMutation.mutateAsync({ newEmail: trimmedNewEmail })
          toast.success(`Check ${trimmedNewEmail} to confirm`, {
            description: 'We sent a verification link to your new address.',
          })
        }}
        onReset={() => {
          setNewEmail('')
          setConfirmNewEmail('')
        }}>
        <FormField label="New email address" htmlFor="profile-new-email" error={newEmailError}>
          <Input
            id="profile-new-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
            placeholder="you@example.com"
          />
        </FormField>
        <FormField
          label="Confirm new email"
          htmlFor="profile-confirm-new-email"
          error={confirmNewEmail.trim() !== '' && !emailsMatch ? 'Email addresses must match.' : undefined}>
          <Input
            id="profile-confirm-new-email"
            type="email"
            value={confirmNewEmail}
            onChange={(e) => setConfirmNewEmail(e.target.value)}
            autoComplete="email"
            placeholder="you@example.com"
          />
        </FormField>
      </FormDialog>
      <SetPasswordDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
        hasPassword={methods?.hasPassword ?? false}
      />
    </Card>
  )
}

interface MethodRowProps {
  label: string
  detail: string
  status: React.ReactNode
  action?: React.ReactNode
}

function MethodRow({ label, detail, status, action }: MethodRowProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{detail}</p>
        {/* div, not p — status may be a Skeleton (renders a div) while loading */}
        <div className="mt-0.5 text-xs text-muted-foreground/70">{status}</div>
      </div>
      {action}
    </div>
  )
}
